import { STATIC_URL } from "../constants";
import { ReportData } from "../types/global.d";

export type ComplaintFieldKey =
  | "recipientTitle"
  | "recipientName"
  | "municipality"
  | "recipientEmail"
  | "senderOrganization"
  | "senderPosition"
  | "senderFullName"
  | "senderContact"
  | "outgoingNumber"
  | "locationDescription"
  | "reportDate"
  | "coordinates"
  | "trashTypes"
  | "amount"
  | "area"
  | "imageEvidence";

export type ComplaintDraftValues = Partial<Record<ComplaintFieldKey, string>>;

export type ComplaintDraftSegment = {
  text: string;
  placeholderKey?: ComplaintFieldKey;
};

export type ComplaintDraft = {
  subject: string;
  subjectSegments: ComplaintDraftSegment[];
  body: string;
  bodySegments: ComplaintDraftSegment[];
  placeholders: ComplaintFieldKey[];
  recipientEmail: string;
};

const PLACEHOLDER_LABELS: Record<ComplaintFieldKey, string> = {
  recipientTitle: "должность адресата",
  recipientName: "ФИО адресата",
  municipality: "город или муниципалитет",
  recipientEmail: "email адресата",
  senderOrganization: "организация отправителя",
  senderPosition: "должность отправителя",
  senderFullName: "ФИО отправителя",
  senderContact: "контакт для ответа",
  outgoingNumber: "исходящий номер",
  locationDescription: "адрес или описание места",
  reportDate: "дата фиксации",
  coordinates: "координаты",
  trashTypes: "тип отходов",
  amount: "объем загрязнения",
  area: "зона загрязнения",
  imageEvidence: "приложите фото или ссылку на фото",
};

const TRASH_TYPE_LABELS: Record<number, string> = {
  1: "бытовые отходы",
  2: "строительные отходы",
  3: "опасные отходы",
  4: "жидкие отходы",
};

const AREA_LABELS: Record<number, string> = {
  1: "до 100 м",
  2: "до 500 м",
  3: "до 1 км",
};

const trimValue = (value?: string | null) => value?.trim() ?? "";

const addText = (segments: ComplaintDraftSegment[], text: string) => {
  if (text) {
    segments.push({ text });
  }
};

const addPlaceholder = (
  segments: ComplaintDraftSegment[],
  key: ComplaintFieldKey,
  placeholders: Set<ComplaintFieldKey>
) => {
  placeholders.add(key);
  segments.push({
    text: `[ЗАПОЛНИТЕ: ${PLACEHOLDER_LABELS[key]}]`,
    placeholderKey: key,
  });
};

const addField = (
  segments: ComplaintDraftSegment[],
  values: ComplaintDraftValues,
  key: ComplaintFieldKey,
  placeholders: Set<ComplaintFieldKey>,
  fallback?: string | null
) => {
  const value = trimValue(values[key] ?? fallback);

  if (value) {
    segments.push({ text: value });
    return;
  }

  addPlaceholder(segments, key, placeholders);
};

const renderSegments = (segments: ComplaintDraftSegment[]) =>
  segments.map((segment) => segment.text).join("");

const formatCoordinates = (report: ReportData) => {
  const latitude = report.geoposition?.latitude;
  const longitude = report.geoposition?.longitude;

  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return "";
  }

  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

export const getComplaintMapUrl = (report: ReportData) => {
  const coordinates = formatCoordinates(report);

  if (!coordinates) {
    return "";
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    coordinates
  )}`;
};

export const getPublicImageUrl = (imagePath: string) => {
  if (!imagePath) {
    return "";
  }

  if (
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://") ||
    imagePath.startsWith("blob:")
  ) {
    return imagePath;
  }

  return `${STATIC_URL}${imagePath.replace(/^\//, "")}`;
};

export const getComplaintImageUrls = (report: ReportData) =>
  (report.images ?? [])
    .filter((image): image is string => Boolean(image))
    .map(getPublicImageUrl);

const getTrashTypesLabel = (report: ReportData) => {
  const labels = (report.trashTypes ?? [])
    .map((type) => TRASH_TYPE_LABELS[type])
    .filter(Boolean);

  return labels.join(", ");
};

const addReportLine = (
  segments: ComplaintDraftSegment[],
  label: string,
  value: string
) => {
  if (value) {
    addText(segments, `${label}: ${value}\n`);
  }
};

export const createComplaintDraft = (
  report: ReportData,
  values: ComplaintDraftValues = {},
  now: Date = new Date()
): ComplaintDraft => {
  const placeholders = new Set<ComplaintFieldKey>();
  const subjectSegments: ComplaintDraftSegment[] = [];
  const bodySegments: ComplaintDraftSegment[] = [];
  const letterDate = now.toLocaleDateString("ru-RU");
  const coordinates = formatCoordinates(report);
  const mapUrl = getComplaintMapUrl(report);
  const imageUrls = getComplaintImageUrls(report);
  const trashTypesLabel = getTrashTypesLabel(report);
  const amountLabel =
    typeof report.amount === "number" && report.amount > 0
      ? `${report.amount} мешков мусора`
      : "";
  const areaLabel =
    typeof report.area === "number" ? AREA_LABELS[report.area] ?? "" : "";
  const locationName = trimValue(report.locationName?.replace(/"/g, ""));
  const reportDate = trimValue(report.date);
  const reportComment = trimValue(report.comment);

  addText(subjectSegments, "Обращение о ликвидации несанкционированной свалки");
  addText(subjectSegments, " - ");
  addField(subjectSegments, values, "municipality", placeholders);

  addField(bodySegments, values, "recipientTitle", placeholders);
  addText(bodySegments, "\n");
  addField(bodySegments, values, "recipientName", placeholders);
  addText(bodySegments, "\n\n");
  addText(bodySegments, "От ");
  addField(bodySegments, values, "senderPosition", placeholders);
  addText(bodySegments, "\n");
  addField(bodySegments, values, "senderOrganization", placeholders);
  addText(bodySegments, "\n");
  addField(bodySegments, values, "senderFullName", placeholders);
  addText(bodySegments, "\nКонтакт для ответа: ");
  addField(bodySegments, values, "senderContact", placeholders);
  addText(bodySegments, "\n\nИсх. N ");
  addField(bodySegments, values, "outgoingNumber", placeholders);
  addText(bodySegments, ` от ${letterDate}\n\n`);
  addText(
    bodySegments,
    "Обращение о ликвидации несанкционированной свалки\n\n"
  );
  addText(bodySegments, "На территории ");
  addField(bodySegments, values, "municipality", placeholders);
  addText(
    bodySegments,
    " обнаружена несанкционированная свалка отходов.\n\n"
  );

  addText(bodySegments, "Сведения о месте загрязнения:\n");
  addText(bodySegments, "- Местоположение: ");
  addField(
    bodySegments,
    values,
    "locationDescription",
    placeholders,
    locationName
  );
  addText(bodySegments, "\n");

  if (coordinates) {
    addReportLine(bodySegments, "- Координаты", coordinates);
  } else {
    addText(bodySegments, "- Координаты: ");
    addPlaceholder(bodySegments, "coordinates", placeholders);
    addText(bodySegments, "\n");
  }

  addReportLine(bodySegments, "- Ссылка на карту", mapUrl);
  addText(bodySegments, "- Дата фиксации: ");
  addField(bodySegments, values, "reportDate", placeholders, reportDate);
  addText(bodySegments, "\n");

  if (trashTypesLabel) {
    addReportLine(bodySegments, "- Тип отходов", trashTypesLabel);
  } else {
    addText(bodySegments, "- Тип отходов: ");
    addPlaceholder(bodySegments, "trashTypes", placeholders);
    addText(bodySegments, "\n");
  }

  if (amountLabel) {
    addReportLine(bodySegments, "- Оценочный объем", amountLabel);
  } else {
    addText(bodySegments, "- Оценочный объем: ");
    addPlaceholder(bodySegments, "amount", placeholders);
    addText(bodySegments, "\n");
  }

  if (areaLabel) {
    addReportLine(bodySegments, "- Зона загрязнения", areaLabel);
  } else {
    addText(bodySegments, "- Зона загрязнения: ");
    addPlaceholder(bodySegments, "area", placeholders);
    addText(bodySegments, "\n");
  }

  if (reportComment) {
    addReportLine(bodySegments, "- Комментарий из отчета", reportComment);
  }

  if (imageUrls.length > 0) {
    addText(bodySegments, "\nФотоматериалы:\n");
    imageUrls.forEach((url, index) => {
      addText(bodySegments, `${index + 1}. ${url}\n`);
    });
  } else {
    addText(bodySegments, "\nФотоматериалы: ");
    addPlaceholder(bodySegments, "imageEvidence", placeholders);
    addText(bodySegments, "\n");
  }

  addText(
    bodySegments,
    "\nПросим организовать проверку указанной территории, принять меры по ликвидации несанкционированной свалки и предотвращению повторного размещения отходов. Также просим направить ответ по результатам рассмотрения обращения и фотоотчет после уборки.\n\n"
  );
  addText(
    bodySegments,
    'Обращение соответствует национальной цели "Экологическое благополучие", установленной Указом Президента Российской Федерации от 7 мая 2024 года N 309, а также задачам национального проекта "Экологическое благополучие" и федерального проекта "Экономика замкнутого цикла".\n\n'
  );
  addText(bodySegments, "С уважением,\n");
  addField(bodySegments, values, "senderPosition", placeholders);
  addText(bodySegments, "\n");
  addField(bodySegments, values, "senderOrganization", placeholders);
  addText(bodySegments, "\n");
  addField(bodySegments, values, "senderFullName", placeholders);
  addText(bodySegments, "\n");
  addField(bodySegments, values, "senderContact", placeholders);

  return {
    subject: renderSegments(subjectSegments),
    subjectSegments,
    body: renderSegments(bodySegments),
    bodySegments,
    placeholders: Array.from(placeholders),
    recipientEmail: trimValue(values.recipientEmail),
  };
};

export const createComplaintMailtoUrl = (draft: ComplaintDraft) => {
  const recipient = encodeURIComponent(draft.recipientEmail);
  const subject = encodeURIComponent(draft.subject);
  const body = encodeURIComponent(draft.body);

  return `mailto:${recipient}?subject=${subject}&body=${body}`;
};

export const getPersistableComplaintValues = (
  values: ComplaintDraftValues
): ComplaintDraftValues => {
  const persistable = { ...values };

  delete persistable.outgoingNumber;
  delete persistable.reportDate;
  delete persistable.locationDescription;
  delete persistable.coordinates;
  delete persistable.trashTypes;
  delete persistable.amount;
  delete persistable.area;
  delete persistable.imageEvidence;

  return persistable;
};
